import { apiClient } from '@/services/api/apiClient';

// Upload response returns minimal file info
export interface UploadFileResponse {
  id: string;
  url: string;
  fileName: string;
}

export interface UploadResponse {
  success: boolean;
  message: string;
  file: UploadFileResponse;
  data?: {
    url: string;
    filename: string;
    size: number;
    mimetype: string;
  };
}

export interface UploadOptions {
  folder?: string;
  showLoader?: boolean;
  backdrop?: boolean;
}

class UploadService {
  /**
   * Upload a single file using the /api/upload/single endpoint
   * @param file - The file to upload
   * @param options - Upload options including folder
   * @returns Promise with upload response
   */
  async uploadSingle(file: File, options: UploadOptions = {}): Promise<UploadResponse> {
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      // Add folder if specified
      if (options.folder) {
        formData.append('folder', options.folder);
      }

      const uploadConfig = {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        showLoader: options.showLoader !== false,
        backdrop: options.backdrop || false,
      };

      const response = await apiClient.post<UploadResponse>(
        '/upload/single',
        formData,
        uploadConfig
      );

      return response;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Failed to upload file');
    }
  }

  /**
   * Upload profile image with specific folder
   * @param file - The image file to upload
   * @returns Promise with upload response
   */
  async uploadProfileImage(file: File): Promise<UploadResponse> {
    return this.uploadSingle(file, {
      folder: 'profiles',
      showLoader: true,
      backdrop: false,
    });
  }

  /**
   * Upload document with specific folder
   * @param file - The document file to upload
   * @returns Promise with upload response
   */
  async uploadDocument(file: File): Promise<UploadResponse> {
    return this.uploadSingle(file, {
      folder: 'documents',
      showLoader: true,
      backdrop: false,
    });
  }

    /**
   * Delete a single file using the /api/upload/single endpoint
   * @param fileId - The fileId to delete
   * @returns Promise with delete response
   */
  async deleteSingleFile(fileId: string): Promise<any> {
    try {
     

      const response = await apiClient.delete<any>(
        '/upload/'+fileId,
      );

      return response;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Failed to delete file');
    }
  }
}

export const uploadService = new UploadService();